import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Benchmarking Vue",
  "description": "",
  "published": "2018-03-05T00:00:00.000Z",
  "redirect_from": ["/blog/benchmarking-vue/swizec/8240"],
  "categories": "Front End, Technical, vue",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding. Usually shorter than 500 words. Often with pictures. Livecoding happens almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{` on multiple channels. You should subscribe to `}<a parentName="em" {...{
          "href": "https://www.youtube.com/user/TheSwizec"
        }}>{`My Youtube`}</a>{` channel to catch me live.`}</em></p>
    <lite-youtube {...{
      "videoid": "Ff5NiCzP0ek",
      "videostartat": "0"
    }}></lite-youtube>
    <p><em parentName="p">{`This article builds on my previous articles about `}<a parentName="em" {...{
          "href": "https://swizec.com/blog/building-interactive-dom-benchmark-preliminary-results/swizec/8219"
        }}>{`Building an interactive DOM benchmark`}</a>{` and `}<a parentName="em" {...{
          "href": "https://swizec.com/blog/seamlessly-render-preact-component-react-project/swizec/8224"
        }}>{`Adding Preact to a React project`}</a></em></p>
    <p>{`I understand why people like Vue, and those people are wrong.`}</p>
    <p>{`But this isn't a rant about Vue. If you like the ease of porting your 2012-era code to a modern framework, Vue is for you. If you're into Mustache templates, Angular directives, and love to split your code into templates, scripts, and styles just like ye olden times, Vue is perfect. Hell, if you love nothing more than to use cumbersome workarounds to avoid JavaScript classes 👉 Vue.`}</p>
    <p>{`You do you. I built a Vue component and hated every minute of it. But it's important to try new things.`}</p>
    <p>{`It took about 2 hours to add Vue to my `}<a parentName="p" {...{
        "href": "https://dom-benchmark.now.sh/"
      }}>{`interactive DOM benchmark`}</a>{`. Half the time I spent bitching about the Angular 1 style templates. The other half, I complained about 2012-era hacks designed to fake classes.`}</p>
    <p>{`Oh, and it took an hour just to get it compiling.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Getting started with Vue is easy they said, you'll have fun they said.`}<br parentName="p"></br><br parentName="p"></br>{`vue-loader installs vue-template-es2015-compiler then complains it can't find vue-template-compiler `}<a parentName="p" {...{
          "href": "https://t.co/m0w4nmp5Tf"
        }}>{`pic.twitter.com/m0w4nmp5Tf`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/970435615450849280"
      }}>{`March 4, 2018`}</a></blockquote>
    <p>{`A wonderful experience all over 👌`}</p>
    <p><strong parentName="p">{`BUT!`}</strong>{` I created my first non-trivial Vue component, a simple benchmark 🎉`}</p>
    <h2 {...{
      "id": "my-first-vue-component"
    }}>{`My first Vue component`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/03/component-screenshot.png",
        "alt": "The Vue benchmark component"
      }}></img></p>
    <p>{`You can see the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/dom-benchmark/blob/master/src/benchmarks/VueBenchmark.vue"
      }}>{`code on GitHub`}</a>{`. There are 5 buttons and a scratchpad area that renders a list of immutable nodes. Much like a chatroom or a newsfeed.`}</p>
    <p>{`Buttons can `}<inlineCode parentName="p">{`prepend`}</inlineCode>{`, `}<inlineCode parentName="p">{`insert`}</inlineCode>{`, `}<inlineCode parentName="p">{`append`}</inlineCode>{`, `}<inlineCode parentName="p">{`drop`}</inlineCode>{`, and `}<inlineCode parentName="p">{`remove`}</inlineCode>{` nodes. Clicks map to methods on the Vue component that manipulate a `}<inlineCode parentName="p">{`nodes`}</inlineCode>{` array in data. The template renders it as a bunch of divs.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<div class="row benchmark-scratchpad">
  <div v-for="k in nodes">{{ k }}</div>
</div>
`}</code></pre>
    <p>{`That `}<inlineCode parentName="p">{`v-for="k in nodes"`}</inlineCode>{` syntax is one of my least favorite parts of Vue. Brings back memories of some Angular 1 projects I worked on. Nightmares lasted months`}<a parentName="p" {...{
        "href": "#fn1"
      }}>{`1`}</a>{`.`}</p>
    <p>{`Click events call instance methods via `}<inlineCode parentName="p">{`v-on:click="func"`}</inlineCode>{`. They manipulate the `}<inlineCode parentName="p">{`nodes`}</inlineCode>{` array and that triggers a re-render. These methods look a lot like to their React counterparts, but cleaner because there's no `}<inlineCode parentName="p">{`setState`}</inlineCode>{` in Vue. I liked that 🤫`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`prepend() {
    this.nodes = [...this.newNodes(), ...this.nodes];
},

append() {
    this.nodes = [...this.nodes, ...this.newNodes()];
},
`}</code></pre>
    <p>{`I think that looks more readable and makes more sense than going through `}<inlineCode parentName="p">{`setState`}</inlineCode>{` in React.`}</p>
    <p><em parentName="p">{`However`}</em>{`, you have to define instance attributes in a `}<inlineCode parentName="p">{`data:`}</inlineCode>{` key and instance methods in a `}<inlineCode parentName="p">{`methods:`}</inlineCode>{` key. But you override Vue's internal instance methods in the normal namespace. 🤨`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`module.exports = {
  // adding attributes here does nothing

  data: function () {
    // this is where you define state
    return {
      nodes: [],
      start: null,
      times: [],
      // ...
    };
  },

  // override Vue instance methods here

  methods: {
    // define custom instance methods here
  },
};
`}</code></pre>
    <p>{`Wat 🤨`}</p>
    <p>{`That's kind of how we used to write code in the old days before JavaScript got better. Did I fall into a time machine?`}</p>
    <p>{`I've heard you can install plugins and tweak configs to get a better approach going, but if it's not the default in the official docs, it doesn't exist. Ask anyone.`}</p>
    <p>{`Some people like this, so here we are. It was a fun experiment, I don't like it at all. ¯`}{`\\`}{`_`}{`(ツ)`}{`_`}{`/¯`}</p>
    <p><strong parentName="p">{`Oh btw`}</strong>{` we render benchmark results using Vue refs and direct DOM manipulation so as to avoid re-rendering via Vue after each render. That would be unfair.`}</p>
    <h2 {...{
      "id": "adding-vue-to-a-nwb-cli-based-project"
    }}>{`Adding Vue to a nwb-cli based project`}</h2>
    <p>{`Getting Vue to compile at all was fun, too. I started the project using `}<a parentName="p" {...{
        "href": "https://github.com/insin/nwb"
      }}>{`nwb-cli`}</a>{`, which is not The Official Way™. You're supposed to use `}<inlineCode parentName="p">{`vue-cli`}</inlineCode>{`, but I didn't want to start a new project.`}</p>
    <p>{`Some finagling with `}<inlineCode parentName="p">{`vue-loader`}</inlineCode>{` was necessary. Two steps 👇`}</p>
    <ol>
      <li parentName="ol">{`install some stuff`}</li>
      <li parentName="ol">{`tweak `}<inlineCode parentName="li">{`nwb.config.js`}</inlineCode>{`.`}</li>
    </ol>
    <h3 {...{
      "id": "install-some-stuff"
    }}>{`Install some stuff`}</h3>
    <pre><code parentName="pre" {...{}}>{`yarn add vue vue-loader vue-template-compiler
`}</code></pre>
    <p>{`You need both `}<inlineCode parentName="p">{`vue-loader`}</inlineCode>{` and `}<inlineCode parentName="p">{`vue-template-compiler`}</inlineCode>{` because vue-loader actually installs `}<inlineCode parentName="p">{`vue-template-es2015-compiler`}</inlineCode>{` then complains that it can't find `}<inlineCode parentName="p">{`vue-template-compiler`}</inlineCode>{`. This is confusing.`}</p>
    <p>{`I installed `}<a parentName="p" {...{
        "href": "https://github.com/akxcv/vuera"
      }}>{`vuera`}</a>{` as well. It gives you a wrapper component so you can render Vue inside your React project. `}<a parentName="p" {...{
        "href": "https://github.com/akxcv/vuera/blob/master/src/wrappers/Vue.js%60"
      }}>{`Vue is kept internally autonomous`}</a>{` so it shouldn't affect the benchmark, but it makes it easier to put all benchmarks in the same project.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// VueBenchmarkWrapper.js

import React from "react";
import { VueWrapper } from "vuera";
import VueBenchmark from "./VueBenchmark.vue";

export default () => <vuewrapper component={VueBenchmark}>;</vuewrapper>;
`}</code></pre>
    <h3 {...{
      "id": "tweak-nwbconfigjs"
    }}>{`Tweak nwb.config.js`}</h3>
    <p>{`After installing Vue, you have to tell Webpack how to use it. That goes in `}<inlineCode parentName="p">{`nwb.config.js`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// nwb.config.js
module.exports = {
  type: "react-app",
  webpack: {
    extra: {
      module: {
        rules: [
          {
            test: /\\.vue$/,
            loader: "vue-loader",
          },
        ],
      },
    },
  },
};
`}</code></pre>
    <p>{`You add a rule that tells Webpack to use `}<inlineCode parentName="p">{`vue-loader`}</inlineCode>{` for files ending with `}<inlineCode parentName="p">{`.vue`}</inlineCode>{`. Same as a normal Webpack config file, just wrapped in `}<inlineCode parentName="p">{`extra:`}</inlineCode>{` so `}<inlineCode parentName="p">{`nwb`}</inlineCode>{` knows what to do.`}</p>
    <p>{`This is where `}<inlineCode parentName="p">{`nwb`}</inlineCode>{` shines over `}<inlineCode parentName="p">{`create-react-app`}</inlineCode>{`. Extra config without eject.`}</p>
    <h2 {...{
      "id": "next-step-for-the-dom-benchmark"
    }}>{`Next step for the DOM benchmark`}</h2>
    <p>{`What I want to do next is going to blow your mind. Maybe.`}</p>
    <p>{`Remember my `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/blockchain-redux-experiment-part-2/swizec/8055"
      }}>{`blockchain-redux`}</a>{` experiment from December? I want to use it to make `}<a parentName="p" {...{
        "href": "https://dom-benchmark.now.sh/"
      }}>{`this DOM benchmark`}</a>{` collaborative.`}</p>
    <p>{`Every time you click a button, it adds to the benchmark blockchain, distributes it to everyone, updates graphs on everyone's page. We're going to see how these benchmarks behave in the real world on real people's computers.`}</p>
    <p>{`That's gonna be fun 🤘`}</p>
    <p><img parentName="p" {...{
        "src": "/eb154423bbc0f9ecd51269c9bc49b559/media-u0v3z2iQLxyHC-giphy.gif",
        "alt": null
      }}></img></p>
    <ol>
      <li parentName="ol">{`This is a dramatization. There were only cold sweats every time I opened the editor to get started. `}<a parentName="li" {...{
          "href": "#ffn1"
        }}>{`↩`}</a></li>
    </ol>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      